import Vue from "vue";
import VueRouter, { RouteConfig, Route } from "vue-router";
import { AuthService, LoginInfo, GLUtil, locationReplace, RoleName } from "commonui";
import { Paths } from "@/util/paths";
import { getModule } from "vuex-module-decorators";
import AppSetting from "@/modules/app-settings";
import { AppViewMode, ApplicationViews, VcsMenuCategory, VcsMenuCategoryPermission, AccessLevel } from "@/util/enum";
import { Spinner } from "@/commonui/store/modules/spinner";
import { getAutorizationDetails } from "@/util/func";
import { isNull, isUndefined, last } from "lodash";
import { CategoryMangerResolver } from "@/views/category-manager/category-manager.resolver";
import { PermissionManagerRouteResolver } from "@/views/user-management/sub-views/permission-manager/permission-manager-resolver";
import { ContentAdminRouteResolver } from "@/views/user-management/sub-views/content-administrator/content-administrator-resolver";
import { TitleListRouteResolver } from "@/views/text-guardian/sub-views/title-list/title-list-resolver";
import { UnitListRouteResolver } from "@/views/text-guardian/sub-views/unit-list/unit-list-resolver";
import { TextResolver } from "@/views/text-guardian/sub-views/title-list/sub-views/text/text-resolver";
import { TagListRouteResolver } from "@/views/text-guardian/sub-views/language-functions/sub-views/tag-list/tag-list-resolver";
import { DetailsReportRouteResolver } from "@/views/text-guardian/sub-views/language-functions/sub-views/details-report/details-report-resolver";
import { TotalReportRouteResolver } from "@/views/text-guardian/sub-views/language-functions/sub-views/total-report/total-report-resolver";
import { TextAnalysisResolver } from "@/views/word-count/sub-views/text-analysis/text-analysis-resolver";
import { WordCountRouteResolver } from "@/views/text-writer/sub-views/text-writer-word-count/text-writer-word-count-resolver";
import { WordPhraseSearchRouteResolver } from "@/views/text-writer/sub-views/word-phrase-search/word-phrase-search-resolver";
import { WordManagerRouteResolver } from "@/views/word-manager/word-manager-resolver";
import { RWTextAnalysisResolver } from "@/views/word-count/sub-views/reader-writer-text-analysis/reader-writer-text-analysis-resolver";
import { TextChangeAnalysisRouteResolver } from "@/views/text-writer/sub-views/text-change-analysis/text-change-analysis-resolver";
import { WordSpecRouteResolver } from "@/views/text-writer/sub-views/word-spec/word-spec-resolver";
import { LFSearchResolver } from "@/views/text-guardian/sub-views/language-functions/sub-views/lf-search/lf-serach-resolver";
import { ObjectiveManagerRouteResolver } from "@/views/objective-manager/objective-manager-resolver";
import { ContentExposureResolver } from "@/views/text-guardian/sub-views/title-list/sub-views/content-exposure/content-exposure-resolver";
import { ContentObjectiveResolver } from "@/views/text-guardian/sub-views/title-list/sub-views/content-objective/content-objective-resolver";
import { MasterPlanResolver } from "@/views/lesson-plan/master-plan/master-plan-resolver";
import { LessonPlanVersionResolver } from "@/views/lesson-plan/lesson-plan-version/version-resolver";
import { TeachingActivitiesResolver } from "@/views/common-entities/sub-views/teaching-activities/teaching-activities-resolver";
import { TransitionsResolver } from "@/views/common-entities/sub-views/transitions/transitions-resolver";
import { CommonRouteResolver } from "@/views/common-entities/common-entities-resolver";
import { LessonPlanListResolver } from "@/views/lesson-plan/lesson-plan-list/list-resolver";
import { LessonPlanTemplateResolver } from "@/views/lesson-plan/template/template-resolver";
import { LessonPlanTemplateEditorResolver } from "@/views/lesson-plan/template/template-editor-resolver";
import { ReportsResolver } from "@/views/reports/reports-resolver";
import { ScopeSequenceResolver } from "@/views/reports/sub-views/scope-sequence/scope-sequence-resolver";
import { UnitOverviewResolver } from "@/views/reports/sub-views/unit-overview/unit-overview-resolver";
import { ComponentObjectivesResolver } from "@/views/reports/sub-views/component-objectives/component-objectives-resolver";
import { VpcOverviewResolver } from "@/views/reports/sub-views/vpc-overview/vpc-overview-resolver";
import { MasterPlanLessonPlanEditorResolver } from "@/views/lesson-plan/master-plan-lesson-plan-editor/master-plan-lesson-plan-editor-resolver";
import { OpenCloseResolver } from "@/views/templates/sub-views/open-close/open-close-resolver";
import { ConversationMasterBuilderResolver } from "@/views/lesson-plan/conversation-master-builder/conversation-master-buider-resolver";
import { ManagerChaptersRouteResolver } from "@/views/text-guardian/sub-views/title-list/sub-views/manage-chapters/manage-chapters-resolver";
import { ConversationBuilderRouteResolver } from "@/views/conversation-builder/conversation-builder.resolver";
import { ComponentConversationResolver } from "@/views/text-guardian/sub-views/title-list/sub-views/conversations/conversations-resolver";
import { REPResolver } from "@/views/text-guardian/sub-views/title-list/sub-views/rep/rep-resolver";
import { ConversationsResolver } from "@/views/list-management/conversations/conversations-resolver";
import { UtteranceEntitiesResolver } from "@/views/templates/sub-views/utterance-entities/utterance-entities-resolver";
import { MediaResolver } from "@/views/text-guardian/sub-views/title-list/sub-views/media/media-resolver";
import { PhonogramManagerRouteResolver } from "@/views/phonogram-manager/phonogram-manager-resolver";
import { RevisionManagerRouteResolver } from "@/views/revision-manager/revision-manager-resolver";
import { LessonPlanActivitiesResolver } from "@/views/active-learn/activities/activities-resolver";
import { AssetListResolver } from "@/views/asset-list/asset-list-resolver";
import { NonCurriculumTitleListRouteResolver } from "@/views/text-guardian/sub-views/non-curriculum/title-list-resolver";
import { FocusWordsManagerRouteResolver } from "@/views/focus-words-manager/focus-words-manager-resolver";
import { FocusWordsAssociationResolver } from "@/views/text-guardian/sub-views/title-list/sub-views/focus-words/focus-words-resolver";
import { ComponentToObjectiveResolver } from "@/views/reports/sub-views/component-to-objective/component-to-objective-resolver";
import { FocusWordsByChapterResolver } from "@/views/reports/sub-views/focuswords-chapter/focuswords-chapter-resolver";
import { MasteryQuestionToObjectiveResolver } from "@/views/reports/sub-views/mastery-question-to-objective/mastery-question-to-objective-resolver";
import { AuditHistoryManagerRouteResolver } from "@/views/audit-history-manager/audit-history-manager-resolver";
import { MediaChangeImpactResolver } from "@/views/reports/sub-views/media-change-impact/media-change-impact-resolver";

Vue.use(VueRouter);

const appSetting = getModule(AppSetting) as AppSetting;
const spinModule = getModule(Spinner) as Spinner;

const routes: Array<VcRouteConfig> = [
	{
		name: "Dashboard",
		component: () => import(/* webpackChunkName: "dashboard" */ `@/views/dashboard/dashboard.vue`),
		path: Paths.Dashborad,
		meta: {
			requiresAuth: true,
			viewId: ApplicationViews.Dashboard
		}
	},
	{
		name: "Exposure Conversation Builder",
		component: () =>
			import(
				/* webpackChunkName: "conversation-builder" */ `@/views/conversation-builder/conversation-builder.vue`
			),
		path: Paths.ExposureConversationBuilder,
		beforeEnter: ConversationBuilderRouteResolver,
		meta: {
			requiresAuth: true,
			viewId: ApplicationViews.ConversationBuilder,
			removeContainer: true,
			containerPadding: false,
			permissionId: VcsMenuCategoryPermission.TeachingPlan
		}
	},
	{
		name: "Content Conversation Builder",
		component: () =>
			import(
				/* webpackChunkName: "conversation-builder" */ `@/views/conversation-builder/conversation-builder.vue`
			),
		path: Paths.ContentConversationBuilder,
		beforeEnter: ConversationBuilderRouteResolver,
		meta: {
			requiresAuth: true,
			viewId: ApplicationViews.ConversationBuilder,
			removeContainer: true,
			containerPadding: false,
			permissionId: VcsMenuCategoryPermission.ComponentConversation
		}
	},
	{
		name: "Master Conversation Builder",
		component: () =>
			import(
				/* webpackChunkName: "conversation-builder" */ `@/views/conversation-builder/conversation-builder.vue`
			),
		path: Paths.MasterConversationBuilder,
		beforeEnter: ConversationBuilderRouteResolver,
		meta: {
			requiresAuth: true,
			viewId: ApplicationViews.ConversationBuilder,
			removeContainer: true,
			containerPadding: false,
			permissionId: VcsMenuCategoryPermission.Conversations
		}
	},
	{
		name: "Permission Manager",
		component: () =>
			import(
				/* webpackChunkName: "permission-management" */ `@/views/user-management/sub-views/permission-manager/permission-manager.vue`
			),
		path: Paths.PermissionManager,
		beforeEnter: PermissionManagerRouteResolver,
		meta: {
			requiresAuth: true,
			viewId: ApplicationViews.Permissions,
			menuId: VcsMenuCategory.UserManagement,
			permissionId: VcsMenuCategoryPermission.Permissions
		}
	},
	{
		name: "Content Administrator",
		component: () =>
			import(
				/* webpackChunkName: "content-management" */ `@/views/user-management/sub-views/content-administrator/content-administrator.vue`
			),
		path: Paths.ContentAdministrator,
		beforeEnter: ContentAdminRouteResolver,
		meta: {
			requiresAuth: true,
			viewId: ApplicationViews.ContentAdministrators,
			menuId: VcsMenuCategory.UserManagement,
			permissionId: VcsMenuCategoryPermission.ContentAdministrators
		}
	},
	{
		name: "Category Manager",
		component: () =>
			import(/* webpackChunkName: "category-manager" */ `@/views/category-manager/category-manager.vue`),
		path: Paths.CategoryManager,
		meta: {
			requiresAuth: true,
			viewId: ApplicationViews.CategoryManager,
			menuId: VcsMenuCategory.ListManagement,
			permissionId: VcsMenuCategoryPermission.CategoryManager
		},
		beforeEnter: CategoryMangerResolver
	},
	{
		name: "Conversations",
		component: () => import(`@/views/list-management/conversations/conversations.vue`),
		path: Paths.Conversations,
		beforeEnter: ConversationsResolver,
		meta: {
			requiresAuth: true,
			viewId: ApplicationViews.Conversations,
			menuId: VcsMenuCategory.ListManagement,
			permissionId: VcsMenuCategoryPermission.Conversations
		}
	},
	{
		name: "Unit List",
		component: () =>
			import(/* webpackChunkName: "text-guardian" */ `@/views/text-guardian/sub-views/unit-list/unit-list.vue`),
		path: Paths.UnitList,
		beforeEnter: UnitListRouteResolver,
		meta: {
			requiresAuth: true,
			viewId: ApplicationViews.UnitList,
			menuId: VcsMenuCategory.ListManagement,
			permissionId: VcsMenuCategoryPermission.UnitList,
			verManagerVisible: false
		}
	},
	{
		name: "Asset List",
		component: () => import(/* webpackChunkName: "asset-list" */ `@/views/asset-list/asset-list.vue`),
		path: Paths.AssetList,
		beforeEnter: AssetListResolver,
		meta: {
			requiresAuth: true,
			viewId: ApplicationViews.AssetList,
			menuId: VcsMenuCategory.ActiveLearn,
			permissionId: VcsMenuCategoryPermission.AssetList,
			verManagerVisible: true
		}
	},
	{
		name: "Components", // Title List
		component: () =>
			import(
				/* webpackChunkName: "text-guardian-title-List" */ `@/views/text-guardian/sub-views/title-list/title-list.vue`
			),
		path: Paths.Components,
		beforeEnter: TitleListRouteResolver,
		meta: {
			requiresAuth: true,
			viewId: ApplicationViews.Components,
			menuId: VcsMenuCategory.CurriculumManagement,
			permissionId: VcsMenuCategoryPermission.Components,
			verManagerVisible: true
		},
		children: [
			{
				name: "EditText",
				component: () =>
					import(
						/* webpackChunkName: "text-guardian--text-edit" */ `@/views/text-guardian/sub-views/title-list/sub-views/text/text.vue`
					),
				path: Paths.EditText,
				beforeEnter: TextResolver,
				meta: {
					requiresAuth: true,
					viewId: ApplicationViews.Text,
					menuId: VcsMenuCategory.CurriculumManagement,
					permissionId: VcsMenuCategoryPermission.Text,
					verManagerVisible: true
				}
			},
			{
				name: "Text",
				component: () =>
					import(
						/* webpackChunkName: "text-guardian-text" */ `@/views/text-guardian/sub-views/title-list/sub-views/text/text.vue`
					),
				path: Paths.Text,
				beforeEnter: TextResolver,
				meta: {
					requiresAuth: true,
					viewId: ApplicationViews.Text,
					menuId: VcsMenuCategory.CurriculumManagement,
					permissionId: VcsMenuCategoryPermission.Text,
					verManagerVisible: true
				}
			},
			{
				name: "EditManageChapters",
				component: () =>
					import(
						/* webpackChunkName: "text-guardian--manageChapters-edit" */ `@/views/text-guardian/sub-views/title-list/sub-views/manage-chapters/manage-chapter.vue`
					),
				path: Paths.EditManageChapters,
				beforeEnter: ManagerChaptersRouteResolver,
				meta: {
					requiresAuth: true,
					viewId: ApplicationViews.ManageChapters,
					menuId: VcsMenuCategory.CurriculumManagement,
					permissionId: VcsMenuCategoryPermission.ManageChapters,
					verManagerVisible: true
				}
			},
			{
				name: "ManageChapters",
				component: () =>
					import(
						/* webpackChunkName: "text-guardian-manageChapters" */ `@/views/text-guardian/sub-views/title-list/sub-views/manage-chapters/manage-chapter.vue`
					),
				path: Paths.ManageChapters,
				beforeEnter: ManagerChaptersRouteResolver,
				meta: {
					requiresAuth: true,
					viewId: ApplicationViews.ManageChapters,
					menuId: VcsMenuCategory.CurriculumManagement,
					permissionId: VcsMenuCategoryPermission.ManageChapters,
					verManagerVisible: true
				}
			},
			{
				name: "ContentObjective",
				component: () =>
					import(
						/* webpackChunkName: "text-guardian-content-objective" */ `@/views/text-guardian/sub-views/title-list/sub-views/content-objective/content-objective.vue`
					),
				path: Paths.ContentObjective,
				beforeEnter: ContentObjectiveResolver,
				meta: {
					requiresAuth: true,
					viewId: ApplicationViews.ContentObjective,
					menuId: VcsMenuCategory.CurriculumManagement,
					permissionId: VcsMenuCategoryPermission.Objectives,
					verManagerVisible: true
				}
			},
			{
				name: "ContentExposure",
				component: () =>
					import(
						/* webpackChunkName: "text-guardian-content-exposure" */ `@/views/text-guardian/sub-views/title-list/sub-views/content-exposure/content-exposure.vue`
					),
				path: Paths.ContentExposure,
				beforeEnter: ContentExposureResolver,
				meta: {
					requiresAuth: true,
					viewId: ApplicationViews.ContentExposure,
					menuId: VcsMenuCategory.CurriculumManagement,
					permissionId: VcsMenuCategoryPermission.TeachingPlan,
					verManagerVisible: true
				}
			},
			{
				name: "REP",
				component: () =>
					import(
						/* webpackChunkName: "text-guardian-rep" */ `@/views/text-guardian/sub-views/title-list/sub-views/rep/rep.vue`
					),
				path: Paths.REP,
				beforeEnter: REPResolver,
				meta: {
					// requiresAuth: true,
					viewId: ApplicationViews.REP,
					menuId: VcsMenuCategory.CurriculumManagement,
					permissionId: VcsMenuCategoryPermission.REP,
					verManagerVisible: true
				}
			},
			{
				name: "ComponentConversation",
				component: () =>
					import(
						/* webpackChunkName: "text-guardian-component-conversation" */ `@/views/text-guardian/sub-views/title-list/sub-views/conversations/conversations.vue`
					),
				path: Paths.ComponentConversation,
				beforeEnter: ComponentConversationResolver,
				meta: {
					requiresAuth: true,
					viewId: ApplicationViews.ComponentConversation,
					menuId: VcsMenuCategory.CurriculumManagement,
					permissionId: VcsMenuCategoryPermission.ComponentConversation,
					verManagerVisible: true
				}
			},
			{
				name: "Media",
				component: () =>
					import(
						/* webpackChunkName: "text-guardian-media" */ `@/views/text-guardian/sub-views/title-list/sub-views/media/media.vue`
					),
				path: Paths.Media,
				beforeEnter: MediaResolver,
				meta: {
					requiresAuth: true,
					viewId: ApplicationViews.Media,
					menuId: VcsMenuCategory.CurriculumManagement,
					permissionId: VcsMenuCategoryPermission.Media,
					verManagerVisible: true
				}
			},
			{
				name: "FocusWords",
				component: () =>
					import(
						/* webpackChunkName: "text-guardian-media" */ `@/views/text-guardian/sub-views/title-list/sub-views/focus-words/focus-words.vue`
					),
				path: Paths.FocusWordsTab,
				beforeEnter: FocusWordsAssociationResolver,
				meta: {
					requiresAuth: true,
					viewId: ApplicationViews.FocusWordsTab,
					menuId: VcsMenuCategory.CurriculumManagement,
					permissionId: VcsMenuCategoryPermission.FocusWordsTab,
					verManagerVisible: true
				}
			}
		]
	},
	{
		name: "Word Manager",
		component: () => import(/* webpackChunkName: "word-manager" */ `@/views/word-manager/word-manager.vue`),
		path: Paths.WordManager,
		beforeEnter: WordManagerRouteResolver,
		meta: {
			requiresAuth: true,
			viewId: ApplicationViews.WordManager,
			menuId: VcsMenuCategory.CurriculumManagement,
			permissionId: VcsMenuCategoryPermission.WordManager,
			verManagerVisible: true
		}
	},
	{
		name: "Phonogram Manager",
		component: () =>
			import(/* webpackChunkName: "word-manager" */ `@/views/phonogram-manager/phonogram-manager.vue`),
		path: Paths.PhonogramManager,
		beforeEnter: PhonogramManagerRouteResolver,
		meta: {
			requiresAuth: true,
			viewId: ApplicationViews.PhonogramManager,
			menuId: VcsMenuCategory.CurriculumManagement,
			permissionId: VcsMenuCategoryPermission.PhonogramManager, // need to change
			verManagerVisible: true
		}
	},
	{
		name: "Revision Manager",
		component: () =>
			import(/* webpackChunkName: "revision-manager" */ `@/views/revision-manager/revision-manager.vue`),
		path: Paths.RevisionManager,
		beforeEnter: RevisionManagerRouteResolver,
		meta: {
			requiresAuth: true,
			viewId: ApplicationViews.RevisionManager,
			menuId: VcsMenuCategory.CurriculumManagement,
			permissionId: VcsMenuCategoryPermission.RevisionManager,
			verManagerVisible: true
		}
	},
	{
		name: "Non Curriculum Components", // Title List
		component: () =>
			import(
				/* webpackChunkName: "text-guardian-title-List" */ `@/views/text-guardian/sub-views/non-curriculum/title-list.vue`
			),
		path: Paths.NonCurriculumComponents,
		beforeEnter: NonCurriculumTitleListRouteResolver,
		meta: {
			requiresAuth: true,
			viewId: ApplicationViews.NonCurriculumComponents,
			menuId: VcsMenuCategory.CurriculumManagement,
			permissionId: VcsMenuCategoryPermission.NonCurriculumComponents,
			verManagerVisible: true
		},
		children: [
			{
				name: "EditText",
				component: () =>
					import(
						/* webpackChunkName: "text-guardian--text-edit" */ `@/views/text-guardian/sub-views/non-curriculum/sub-views/text/text.vue`
					),
				path: Paths.NonCurriculumComponentsEditText,
				beforeEnter: TextResolver,
				meta: {
					requiresAuth: true,
					viewId: ApplicationViews.Text,
					menuId: VcsMenuCategory.CurriculumManagement,
					permissionId: VcsMenuCategoryPermission.Text,
					verManagerVisible: true
				}
			},
			{
				name: "Text",
				component: () =>
					import(
						/* webpackChunkName: "text-guardian-text" */ `@/views/text-guardian/sub-views/non-curriculum/sub-views/text/text.vue`
					),
				path: Paths.NonCurriculumComponentsText,
				beforeEnter: TextResolver,
				meta: {
					requiresAuth: true,
					viewId: ApplicationViews.Text,
					menuId: VcsMenuCategory.CurriculumManagement,
					permissionId: VcsMenuCategoryPermission.Text,
					verManagerVisible: true
				}
			},
			{
				name: "Media",
				component: () =>
					import(
						/* webpackChunkName: "text-guardian-media" */ `@/views/text-guardian/sub-views/non-curriculum/sub-views/media/media.vue`
					),
				path: Paths.NonCurriculumComponentsMedia,
				beforeEnter: MediaResolver,
				meta: {
					requiresAuth: true,
					viewId: ApplicationViews.Media,
					menuId: VcsMenuCategory.CurriculumManagement,
					permissionId: VcsMenuCategoryPermission.Media,
					verManagerVisible: true
				}
			}
		]
	},
	{
		name: "Focus Words",
		component: () =>
			import(/* webpackChunkName: "focus-words-manager" */ `@/views/focus-words-manager/focus-words-manager.vue`),
		path: Paths.FocusWords,
		beforeEnter: FocusWordsManagerRouteResolver,
		meta: {
			requiresAuth: true,
			viewId: ApplicationViews.FocusWords,
			menuId: VcsMenuCategory.CurriculumManagement,
			permissionId: VcsMenuCategoryPermission.FocusWords,
			verManagerVisible: true
		}
	},
	{
		name: "Audit History",
		component: () =>
			import(
				/* webpackChunkName: "audit-history-manager" */ `@/views/audit-history-manager/audit-history-manager.vue`
			),
		path: Paths.AuditHistory,
		beforeEnter: AuditHistoryManagerRouteResolver,
		meta: {
			requiresAuth: true,
			viewId: ApplicationViews.AuditHistory,
			menuId: VcsMenuCategory.AuditHistory,
			permissionId: VcsMenuCategoryPermission.AuditHistory,
			verManagerVisible: false
		}
	},
	{
		name: "Objective Management",
		component: () =>
			import(/* webpackChunkName: "objective-manager" */ `@/views/objective-manager/objective-manager.vue`),
		path: Paths.ObjectiveManager,
		beforeEnter: ObjectiveManagerRouteResolver,
		meta: {
			requiresAuth: true,
			viewId: ApplicationViews.ObjectiveManager,
			menuId: VcsMenuCategory.ListManagement,
			permissionId: VcsMenuCategoryPermission.ObjectiveManager,
			verManagerVisible: true
		}
	},
	{
		name: "Common Entities",
		component: () =>
			import(/* webpackChunkName: "common-entities" */ `@/views/common-entities/common-entities.vue`),
		path: Paths.TeachingActivities,
		beforeEnter: CommonRouteResolver,
		meta: {
			requiresAuth: true,
			viewId: ApplicationViews.CommonEntities,
			menuId: VcsMenuCategory.LessonPlan,
			permissionId: VcsMenuCategoryPermission.CommonEntities,
			verManagerVisible: true
		},
		children: [
			{
				name: "Teaching Activities",
				component: () =>
					import(`@/views/common-entities/sub-views/teaching-activities/teaching-activities.vue`),
				path: Paths.TeachingActivities,
				beforeEnter: TeachingActivitiesResolver,
				meta: {
					requiresAuth: true,
					viewId: ApplicationViews.TeachingActivities,
					menuId: VcsMenuCategory.LessonPlan,
					permissionId: VcsMenuCategoryPermission.CommonEntities,
					verManagerVisible: true
				}
			},
			{
				name: "Transitions",
				component: () => import(`@/views/common-entities/sub-views/transitions/transitions.vue`),
				path: Paths.Transitions,
				beforeEnter: TransitionsResolver,
				meta: {
					requiresAuth: true,
					viewId: ApplicationViews.Transitions,
					menuId: VcsMenuCategory.LessonPlan,
					permissionId: VcsMenuCategoryPermission.CommonEntities,
					verManagerVisible: true
				}
			}
		]
	},
	{
		name: "Reports",
		component: () => import(/* webpackChunkName: "reports" */ `@/views/reports/reports.vue`),
		path: Paths.ScopeSequence,
		beforeEnter: ReportsResolver,
		meta: {
			requiresAuth: true,
			viewId: ApplicationViews.Reports,
			menuId: VcsMenuCategory.LessonPlan,
			permissionId: VcsMenuCategoryPermission.Reports,
			verManagerVisible: true
		},
		children: [
			{
				name: "Scope And Sequence",
				component: () => import(`@/views/reports/sub-views/scope-sequence/scope-sequence.vue`),
				path: Paths.ScopeSequence,
				beforeEnter: ScopeSequenceResolver,
				meta: {
					requiresAuth: true,
					viewId: ApplicationViews.ScopeSequence,
					menuId: VcsMenuCategory.LessonPlan,
					permissionId: VcsMenuCategoryPermission.Reports,
					verManagerVisible: true
				}
			},
			{
				name: "Component Objectives",
				component: () => import(`@/views/reports/sub-views/component-objectives/component-objectives.vue`),
				path: Paths.ComponentObjectives,
				beforeEnter: ComponentObjectivesResolver,
				meta: {
					requiresAuth: true,
					viewId: ApplicationViews.ComponentObjectives,
					menuId: VcsMenuCategory.LessonPlan,
					permissionId: VcsMenuCategoryPermission.Reports,
					verManagerVisible: true
				}
			},
			{
				name: "Unit Overview",
				component: () => import(`@/views/reports/sub-views/unit-overview/unit-overview.vue`),
				path: Paths.UnitOverview,
				beforeEnter: UnitOverviewResolver,
				meta: {
					requiresAuth: true,
					viewId: ApplicationViews.UnitOverview,
					menuId: VcsMenuCategory.LessonPlan,
					permissionId: VcsMenuCategoryPermission.Reports,
					verManagerVisible: true
				}
			},
			{
				name: "VPC Overview",
				component: () => import(`@/views/reports/sub-views/vpc-overview/vpc-overview.vue`),
				path: Paths.VpcOverview,
				beforeEnter: VpcOverviewResolver,
				meta: {
					requiresAuth: true,
					viewId: ApplicationViews.VpcOverview,
					menuId: VcsMenuCategory.LessonPlan,
					permissionId: VcsMenuCategoryPermission.Reports,
					verManagerVisible: true
				}
			},
			{
				name: "Component-Objective",
				component: () => import(`@/views/reports/sub-views/component-to-objective/component-to-objective.vue`),
				path: Paths.ComponentToObjective,
				beforeEnter: ComponentToObjectiveResolver,
				meta: {
					requiresAuth: true,
					viewId: ApplicationViews.ComponentToObjective,
					menuId: VcsMenuCategory.LessonPlan,
					permissionId: VcsMenuCategoryPermission.Reports,
					verManagerVisible: true
				}
			},
			{
				name: "Mastery Question-Objective",
				component: () =>
					import(`@/views/reports/sub-views/mastery-question-to-objective/mastery-question-to-objective.vue`),
				path: Paths.MasteryQuestionToObjective,
				beforeEnter: MasteryQuestionToObjectiveResolver,
				meta: {
					requiresAuth: true,
					viewId: ApplicationViews.MasteryQuestionToObjective,
					menuId: VcsMenuCategory.LessonPlan,
					permissionId: VcsMenuCategoryPermission.Reports,
					verManagerVisible: true
				}
			},
			{
				name: "FocusWords-Chapter",
				component: () => import(`@/views/reports/sub-views/focuswords-chapter/focuswords-chapter.vue`),
				path: Paths.FocusWordsByChapter,
				beforeEnter: FocusWordsByChapterResolver,
				meta: {
					requiresAuth: true,
					viewId: ApplicationViews.FocusWordsByChapter,
					menuId: VcsMenuCategory.LessonPlan,
					permissionId: VcsMenuCategoryPermission.Reports,
					verManagerVisible: true
				}
			},
			{
				name: "Media-Change-Impact",
				component: () => import(`@/views/reports/sub-views/media-change-impact/media-change-impact.vue`),
				path: Paths.MediaChangeImpact,
				beforeEnter: MediaChangeImpactResolver,
				meta: {
					requiresAuth: true,
					viewId: ApplicationViews.MediaChangeImpact,
					menuId: VcsMenuCategory.LessonPlan,
					permissionId: VcsMenuCategoryPermission.Reports,
					verManagerVisible: true
				}
			}
		]
	},
	{
		name: "Lesson Plan Version",
		component: () => import(`@/views/lesson-plan/lesson-plan-version/version.vue`),
		path: Paths.LessonPlanVersion,
		beforeEnter: LessonPlanVersionResolver,
		meta: {
			requiresAuth: true,
			viewId: ApplicationViews.LessonPlanVersion,
			menuId: VcsMenuCategory.LessonPlan,
			permissionId: VcsMenuCategoryPermission.LessonPlanVersion,
			verManagerVisible: true,
			appBreadCrumbVisible: true
		}
	},
	{
		name: "Lesson Plan List",
		component: () => import(`@/views/lesson-plan/lesson-plan-list/list.vue`),
		path: Paths.LessonPlanList,
		beforeEnter: LessonPlanListResolver,
		meta: {
			requiresAuth: true,
			viewId: ApplicationViews.LessonPlanList,
			menuId: VcsMenuCategory.LessonPlan,
			permissionId: VcsMenuCategoryPermission.LessonPlanList,
			verManagerVisible: true,
			appBreadCrumbVisible: true
		}
	},
	{
		name: "Student Actvities",
		component: () => import(`@/views/active-learn/activities/activities.vue`),
		path: Paths.StudentActivities,
		beforeEnter: LessonPlanActivitiesResolver,
		meta: {
			requiresAuth: true,
			viewId: ApplicationViews.StudentActivities,
			menuId: VcsMenuCategory.ActiveLearn,
			permissionId: VcsMenuCategoryPermission.StudentActivities,
			verManagerVisible: false
		}
	},
	{
		name: "Lesson Plan List With Params",
		component: () => import(`@/views/lesson-plan/lesson-plan-list/list.vue`),
		path: Paths.LessonPlanListWithParams,
		beforeEnter: LessonPlanListResolver,
		meta: {
			requiresAuth: true,
			viewId: ApplicationViews.LessonPlanList,
			menuId: VcsMenuCategory.LessonPlan,
			permissionId: VcsMenuCategoryPermission.LessonPlanList,
			verManagerVisible: true,
			appBreadCrumbVisible: true
		}
	},
	{
		name: "Master Plan",
		component: () => import(`@/views/lesson-plan/master-plan/master-plan.vue`),
		path: Paths.MasterPlan,
		beforeEnter: MasterPlanResolver,
		meta: {
			requiresAuth: true,
			viewId: ApplicationViews.MasterPlan,
			menuId: VcsMenuCategory.LessonPlan,
			permissionId: VcsMenuCategoryPermission.LessonPlanList,
			verManagerVisible: true,
			appBreadCrumbVisible: true
		}
	},
	{
		name: "Conversation Master Plan",
		component: () => import(`@/views/lesson-plan/conversation-master-builder/conversation-master-builder.vue`),
		path: Paths.ConversationMasterBuilder,
		beforeEnter: ConversationMasterBuilderResolver,
		meta: {
			requiresAuth: true,
			viewId: ApplicationViews.ConversationMasterBuilder,
			menuId: VcsMenuCategory.LessonPlan,
			permissionId: VcsMenuCategoryPermission.LessonPlanList,
			verManagerVisible: true
		}
	},
	{
		name: "Master Plan Lesson Plan Editor",
		component: () =>
			import(`@/views/lesson-plan/master-plan-lesson-plan-editor/master-plan-lesson-plan-editor.vue`),
		path: Paths.MasterPlanLessonPlanEditor,
		beforeEnter: MasterPlanLessonPlanEditorResolver,
		meta: {
			requiresAuth: true,
			viewId: ApplicationViews.MasterPlanLessonPlanEditor,
			menuId: VcsMenuCategory.LessonPlan,
			permissionId: VcsMenuCategoryPermission.LessonPlanList,
			verManagerVisible: true,
			appBreadCrumbVisible: true
		}
	},
	{
		name: "Lesson Plan Template Home",
		component: () => import(`@/views/lesson-plan/template/home.vue`),
		path: Paths.LessonPlanTemplate,
		beforeEnter: LessonPlanTemplateResolver,
		meta: {
			requiresAuth: true,
			viewId: ApplicationViews.LessonPlanTemplate,
			menuId: VcsMenuCategory.LessonPlan,
			permissionId: VcsMenuCategoryPermission.LessonPlanTemplate
		},
		children: [
			{
				name: "Lesson Plan Template",
				component: () => import(`@/views/lesson-plan/template/template-list.vue`),
				path: Paths.LessonPlanTemplate,
				beforeEnter: LessonPlanTemplateResolver,
				meta: {
					requiresAuth: true,
					viewId: ApplicationViews.LessonPlanTemplate,
					menuId: VcsMenuCategory.LessonPlan,
					permissionId: VcsMenuCategoryPermission.LessonPlanTemplate
				}
			},
			{
				name: "Add Lesson Plan Template",
				component: () =>
					import(
						/* webpackChunkName: "lesson-plan-template-add" */ `@/views/lesson-plan/template/template-editor.vue`
					),
				path: Paths.AddLessonPlanTemplate,
				beforeEnter: LessonPlanTemplateEditorResolver,
				meta: {
					requiresAuth: true,
					viewId: ApplicationViews.LessonPlanTemplateEditor,
					menuId: VcsMenuCategory.LessonPlan,
					permissionId: VcsMenuCategoryPermission.LessonPlanTemplate,
					allowedForAccessLevel: [AccessLevel.Full]
				}
			},
			{
				name: "Edit Lesson Plan Template",
				component: () =>
					import(
						/* webpackChunkName: "lesson-plan-template-edit" */ `@/views/lesson-plan/template/template-editor.vue`
					),
				path: Paths.EditLessonPlanTemplate,
				beforeEnter: LessonPlanTemplateEditorResolver,
				meta: {
					requiresAuth: true,
					viewId: ApplicationViews.LessonPlanTemplateEditor,
					menuId: VcsMenuCategory.LessonPlan,
					permissionId: VcsMenuCategoryPermission.LessonPlanTemplate,
					allowedForAccessLevel: [AccessLevel.Full]
				}
			}
		]
	},
	{
		name: "Tag List",
		component: () =>
			import(
				/* webpackChunkName: "text-guardian" */ `@/views/text-guardian/sub-views/language-functions/sub-views/tag-list/tag-list.vue`
			),
		path: Paths.LanguageFunctionTagList,
		meta: {
			requiresAuth: true,
			viewId: ApplicationViews.TagList,
			menuId: VcsMenuCategory.LanguageFunctionManagement,
			permissionId: VcsMenuCategoryPermission.TagList,
			verManagerVisible: true
		}
	},
	{
		name: "Total Report",
		component: () =>
			import(
				/* webpackChunkName: "text-guardian" */ `@/views/text-guardian/sub-views/language-functions/sub-views/total-report/total-report.vue`
			),
		path: Paths.TotalReport,
		beforeEnter: TotalReportRouteResolver,
		meta: {
			requiresAuth: true,
			viewId: ApplicationViews.TagUsageStatistics,
			menuId: VcsMenuCategory.LanguageFunctionManagement,
			permissionId: VcsMenuCategoryPermission.TagUsageStatistics,
			verManagerVisible: true
		}
	},
	{
		name: "Details Report",
		component: () =>
			import(
				/* webpackChunkName: "text-guardian" */ `@/views/text-guardian/sub-views/language-functions/sub-views/details-report/details-report.vue`
			),
		path: Paths.DetailsReport,
		beforeEnter: DetailsReportRouteResolver,
		meta: {
			requiresAuth: true,
			viewId: ApplicationViews.TagUsageDetails,
			menuId: VcsMenuCategory.LanguageFunctionManagement,
			permissionId: VcsMenuCategoryPermission.TagUsageDetails,
			verManagerVisible: true
		}
	},
	{
		name: "LF Search",
		component: () =>
			import(
				/* webpackChunkName: "text-guardian" */ `@/views/text-guardian/sub-views/language-functions/sub-views/lf-search/lf-search.vue`
			),
		path: Paths.LFSearch,
		beforeEnter: LFSearchResolver,
		meta: {
			requiresAuth: true,
			viewId: ApplicationViews.TagSearch,
			menuId: VcsMenuCategory.LanguageFunctionManagement,
			permissionId: VcsMenuCategoryPermission.TagSearch,
			verManagerVisible: true
		}
	},
	{
		name: "Text Analysis",
		component: () =>
			import(/* webpackChunkName: "word-count" */ `@/views/word-count/sub-views/text-analysis/text-analysis.vue`),
		path: Paths.TextAnalysis,
		meta: {
			requiresAuth: true,
			viewId: ApplicationViews.NewTextAnalysis,
			menuId: VcsMenuCategory.TextAnalysis,
			permissionId: VcsMenuCategoryPermission.NewTextAnalysis,
			verManagerVisible: true
		},
		beforeEnter: TextAnalysisResolver
	},
	{
		name: "Reader/Writer Text Analysis",
		component: () =>
			import(
				/* webpackChunkName: "word-count" */ `@/views/word-count/sub-views/reader-writer-text-analysis/reader-writer-text-analysis.vue`
			),
		path: Paths.ReaderWriterTextAnalysis,
		meta: {
			requiresAuth: true,
			viewId: ApplicationViews.ReaderWriterAnalysis,
			menuId: VcsMenuCategory.TextAnalysis,
			permissionId: VcsMenuCategoryPermission.ReaderWriterAnalysis,
			verManagerVisible: true
		},
		beforeEnter: RWTextAnalysisResolver
	},
	{
		name: "Text Change Analysis",
		component: () =>
			import(
				/* webpackChunkName: "text-writer" */ `@/views/text-writer/sub-views/text-change-analysis/text-change-analysis.vue`
			),
		path: Paths.TextChangeAnalysis,
		beforeEnter: TextChangeAnalysisRouteResolver,
		meta: {
			requiresAuth: true,
			viewId: ApplicationViews.TextChangeAnalysis,
			menuId: VcsMenuCategory.TextAnalysis,
			permissionId: VcsMenuCategoryPermission.TextChangeAnalysis,
			verManagerVisible: true
		}
	},
	{
		name: "Text Writer Word Count",
		component: () =>
			import(
				/* webpackChunkName: "text-writer" */ `@/views/text-writer/sub-views/text-writer-word-count/text-writer-word-count.vue`
			),
		path: Paths.TextWriterWordCount,
		beforeEnter: WordCountRouteResolver,
		meta: {
			requiresAuth: true,
			viewId: ApplicationViews.WordCount,
			menuId: VcsMenuCategory.Vocabulary,
			permissionId: VcsMenuCategoryPermission.WordCount,
			verManagerVisible: true
		}
	},
	{
		name: "Word Phrase Search",
		component: () =>
			import(
				/* webpackChunkName: "text-writer" */ `@/views/text-writer/sub-views/word-phrase-search/word-phrase-search.vue`
			),
		path: Paths.WordPhraseSearch,
		beforeEnter: WordPhraseSearchRouteResolver,
		meta: {
			requiresAuth: true,
			viewId: ApplicationViews.WordPhraseSearch,
			menuId: VcsMenuCategory.Vocabulary,
			permissionId: VcsMenuCategoryPermission.WordPhraseSearch,
			verManagerVisible: true
		}
	},
	{
		name: "Word Spec",
		component: () =>
			import(/* webpackChunkName: "text-writer" */ `@/views/text-writer/sub-views/word-spec/word-spec.vue`),
		path: Paths.WordSpec,
		beforeEnter: WordSpecRouteResolver,
		meta: {
			requiresAuth: true,
			viewId: ApplicationViews.WordDetails,
			menuId: VcsMenuCategory.Vocabulary,
			permissionId: VcsMenuCategoryPermission.WordDetails,
			verManagerVisible: true
		}
	},
	{
		name: "Conversation Templates",
		component: () => import(`@/views/templates/templates.vue`),
		path: Paths.ConversationTemplates,
		meta: {
			requiresAuth: true,
			viewId: ApplicationViews.ConversationTemplates,
			menuId: VcsMenuCategory.ListManagement,
			verManagerVisible: false,
			permissionId: VcsMenuCategoryPermission.ConversationTemplates
		},
		children: [
			{
				name: "Fallback",
				component: () => import(`@/views/templates/sub-views/fallback/fallback.vue`),
				path: Paths.Fallback,
				meta: {
					requiresAuth: true,
					viewId: ApplicationViews.Fallback,
					menuId: VcsMenuCategory.ListManagement,
					permissionId: VcsMenuCategoryPermission.Fallback,
					verManagerVisible: false,
					containerPadding: false
				}
			},
			{
				name: "OpenClose",
				component: () => import(`@/views/templates/sub-views/open-close/open-close.vue`),
				path: Paths.OpenClose,
				beforeEnter: OpenCloseResolver,
				meta: {
					requiresAuth: true,
					viewId: ApplicationViews.OpenClose,
					menuId: VcsMenuCategory.ListManagement,
					permissionId: VcsMenuCategoryPermission.OpenClose,
					verManagerVisible: false,
					containerPadding: false
				}
			},
			{
				name: "Utterance Entities",
				component: () => import(`@/views/templates/sub-views/utterance-entities/utterance-entities.vue`),
				path: Paths.UtteranceEntities,
				//beforeEnter:UtteranceEntitiesResolver,
				meta: {
					requiresAuth: true,
					viewId: ApplicationViews.UtteranceEntities,
					menuId: VcsMenuCategory.ListManagement,
					permissionId: VcsMenuCategoryPermission.UtteranceEntities,
					verManagerVisible: false,
					containerPadding: false
				}
			},
			{
				name: "Common Intents",
				component: () => import(`@/views/templates/sub-views/common-intents/common-intents.vue`),
				path: Paths.CommonIntents,
				meta: {
					requiresAuth: true,
					viewId: ApplicationViews.CommonIntents,
					menuId: VcsMenuCategory.ListManagement,
					permissionId: VcsMenuCategoryPermission.CommonIntents,
					verManagerVisible: false,
					containerPadding: false
				}
			}
		]
	},
	{
		name: "Not Found",
		component: () => import(/* webpackChunkName: "not-found" */ `@/views/not-found/not-found.vue`),
		path: Paths.NotFound,
		meta: {
			notFound: true
		}
	}
];

interface VcRouteConfig extends RouteConfig {
	meta: Partial<{
		requiresAuth: boolean;
		viewId: ApplicationViews;
		verManagerVisible: boolean;
		menuId: VcsMenuCategory;
		notFound: boolean;
		permissionId: VcsMenuCategoryPermission | null;
		/** Default value is true  */
		containerPadding: boolean;
		removeContainer: boolean;
		/** Default value is false */
		appBreadCrumbVisible: boolean;
	}>;
}

const router = new VueRouter({
	mode: "history",
	base: process.env.BASE_URL,
	routes
});

const verifySession = () => {
	function isTokenExpired() {
		const { getTokenParam } = AuthService.getTokenUrlParams();
		const exp = getTokenParam("id_token").exp;
		return !GLUtil.isNullOrUndefined(exp) && new Date(exp * 1000) < new Date();
	}
	if (isTokenExpired()) {
		const abortSignin = () => {
			locationReplace("/");
			return Promise.reject();
		};
		return AuthService.clearState()
			.then((_) => abortSignin())
			.catch((_) => abortSignin());
	}
	AuthService.trySetSigninVerifyToken();
	return Promise.resolve();
};

const routeAuth = (loginInfo: LoginInfo, to: Route, next: any) => {
	if (!loginInfo.loggedin || GLUtil.isExpired(loginInfo) || !AuthService.hasPermissions(loginInfo)) {
		if (!spinModule.splash) {
			spinModule.setSplash(true);
		}
		AuthService.storePagethenSigninRedirect();
	} else {
		if (to.meta) {
			const roles = loginInfo?.profile?.roles;
			const isSadminOrCAdmin =
				Array.isArray(roles) && roles.some((r) => r === RoleName.systemAdmin || r === RoleName.contentAdmin);
			if (!isSadminOrCAdmin) {
				appSetting.setAppMode(AppViewMode.UnAuthorized);
				return;
			}
			if (appSetting.mode !== AppViewMode.Authorized) {
				appSetting.setAppMode(AppViewMode.Authorized);
			}

			if (spinModule.splash) {
				spinModule.setSplash(false);
			}

			if (to.matched && to.matched.length > 0) {
				appSetting.setAppMenu(to.matched[0]?.meta?.viewId);
				appSetting.setMenuCategory(to.matched[0]?.meta?.menuId);
				appSetting.setMangerVisible(last(to.matched)!.meta?.verManagerVisible);
				appSetting.setAppBreadCrumbVisible(last(to.matched)!.meta?.appBreadCrumbVisible);
			}

			const authInfo = getAutorizationDetails(to.meta, loginInfo.profile.permissions);
			if (authInfo.isAuthorized && (isUndefined(authInfo.navigateTo) || isNull(authInfo.navigateTo))) {
				appSetting.setChildMenu(to.meta?.viewId);
				next();
			} else if (authInfo.isAuthorized && !isUndefined(authInfo.navigateTo) && !isNull(authInfo.navigateTo)) {
				to.path === authInfo.navigateTo ? next() : next({ path: authInfo.navigateTo, replace: true });
			} else {
				appSetting.setAppMode(AppViewMode.UnAuthorized);
			}
		} else {
			appSetting.setAppMode(AppViewMode.UnAuthorized);
		}
	}
};

router.beforeEach((to, from, next) => {
	if (to.matched.some((record) => record.meta.requiresAuth)) {
		if (window.location.href.indexOf("oidc/signout") !== -1) {
			AuthService.signoutRedirectCallback()
				.then((err) => {
					AuthService.clearSignStorage();
					const page = AuthService.getPageAfterSignout();
					locationReplace(page ? page.afterSignout || page.currentSignout : "/");
				})
				.catch((e) => {
					console.error(e);
				});
		} else if (window.location.href.indexOf("id_token") !== -1) {
			verifySession().then(() => {
				AuthService.signinRedirectCallback().then((user) => {
					try {
						const page = AuthService.getPageAfterSignin();
						const processedUser = AuthService.processUser(user);
						AuthService.setLoginInfo(true, processedUser, () => {
							const replaceToSite = () =>
								locationReplace(page ? page.afterSignin || page.shouldSignin : "/");
							try {
								return replaceToSite();
							} catch (e) {
								return replaceToSite();
							}
						});
					} catch (e) {
						locationReplace("/");
					}
				});
			});
		} else {
			const loginInfo = AuthService.getLoginInfo();
			if (loginInfo && loginInfo.loggedin) {
				routeAuth(loginInfo, to, next);
			} else {
				AuthService.useLocalStoreToLogin().then((loginInfo) => routeAuth(loginInfo, to, next));
			}
		}
	} else if (to.matched.some((record) => record.meta.notFound)) {
		appSetting.setAppMode(AppViewMode.NotFound);
		next();
	} else {
		appSetting.setAppMode(AppViewMode.Blank);
		next();
	}
});

export default router;
